import { CustomFieldValueAttributes } from '@/types'
import { UpdateCustomFieldValueMutation } from './__generated__/update_mutation'
import { Mutation as UpdateMutation } from './update_mutation'
import { useForm } from '@/helpers/useForm'
import { EditCustomFieldValueQuery } from './__generated__/edit_query'
import { Query as EditQuery } from './edit_query'
import { Inline as Loading } from '@/components/loading'
import { useEffect, useState } from 'react'
import { Query as TableQuery } from './table_query'
import { Button } from '../base/button'
import { DeleteDialog } from '@/components/delete_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'
import { Inline as Error } from '@/components/error'
import { Query as OptionsQuery } from './options_query'
import { CustomFieldValueOptionsQuery } from './__generated__/options_query'
import { useQuery } from '@apollo/client'
import { valueField } from './value_field'

type CustomFieldValue = EditCustomFieldValueQuery['customFieldValues']['nodes'][0]

interface Props {
  id: string
  onSuccess: () => void
}

export const Form = ({ id, onSuccess }: Props) => {
  const {
    save,
    saveSuccess,
    formBuilder,
    queryError,
    queryLoading,
    mutationLoading,
    attributes,
    setAttributes,
    fieldErrors
  } = useForm<UpdateCustomFieldValueMutation, CustomFieldValueAttributes, EditCustomFieldValueQuery, CustomFieldValue>({
    id,
    initializer: customFieldValue => ({
      customFieldId: customFieldValue.customField.id,
      value: customFieldValue.value,
      resourceId: customFieldValue.resourceId,
      resourceType: customFieldValue.resourceType,
    }),
    query: EditQuery,
    queryKey: 'customFieldValues',
    mutation: UpdateMutation,
    resultKey: 'updateCustomFieldValue',
    refetchQueries: [TableQuery]
  })
  const [showDelete, setShowDelete] = useState(false)

  useEffect(() => { if (saveSuccess) onSuccess() }, [saveSuccess, onSuccess])

  const { data, loading, error } = useQuery<CustomFieldValueOptionsQuery>(
    OptionsQuery,
    { variables: { filters: { resourceTypeEq: attributes.resourceType } } }
  )

  const onDelete = () => {
    setShowDelete(false)
    onSuccess()
  }

  if (loading || queryLoading) return <Loading />

  return (
    <>
      { queryError && <Error message={queryError} /> }
      { error && <Error message={error.message} /> }
      <div className="flex flex-col gap-6">
        { formBuilder.buildQueryError() }
        { valueField({
          customFields: data?.customFields || { nodes: [] },
          attributes,
          setAttributes,
          fieldErrors
        }) }
        <div className="flex flex-row justify-between">
          <Button color="red" onClick={() => setShowDelete(true) }>Delete</Button>
          { formBuilder.saveButton(save, mutationLoading, saveSuccess) }
        </div>
        <DeleteDialog
          id={id}
          mutation={DeleteMutation}
          open={showDelete}
          refetchQueries={[TableQuery]}
          setOpen={setShowDelete}
          onSuccess={onDelete}
        />
      </div>
    </>
  )
}
